// apps/web/src/pages/admin/AuditLogsPage.tsx

import React, { useState } from 'react';
import { trpc } from '@/trpc/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Terminal, ScrollText, InfoIcon, ChevronLeft, ChevronRight, Search, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { AuditAction } from '@logistics-platform/types'; // Ensure this is exported from types or manually define if needed
import { useDebounce } from '@/hooks/useDebounce';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const AuditLogsPage: React.FC = () => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedAction, setSelectedAction] = useState<AuditAction | undefined>(undefined);
    const debouncedSearchQuery = useDebounce(searchQuery, 500);

    const { data, isLoading, error } = trpc.admin.getAuditLogs.useQuery({
        page,
        pageSize,
        search: debouncedSearchQuery,
        action: selectedAction,
        orderBy: 'timestamp',
        orderDirection: 'desc',
    });

    const logs = data?.logs || [];
    const totalLogs = data?.totalCount || 0;
    const totalPages = Math.ceil(totalLogs / pageSize);

    const formatDetails = (details?: any): string => {
        if (!details) return '';
        const relevantDetails = { ...details };
        delete relevantDetails.ipAddress;
        delete relevantDetails.userAgent;
        return JSON.stringify(relevantDetails, null, 2);
    };

    if (error) {
        return (
            <div className="container mx-auto py-12 px-4 max-w-7xl">
                <Alert variant="destructive">
                    <Terminal className="h-4 w-4" />
                    <AlertTitle>Error Loading Logs</AlertTitle>
                    <AlertDescription>{error.message}</AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div className="container mx-auto py-6 px-2 sm:px-4 max-w-full animate-fade-in">
            <Card className="glass">
                <CardHeader className="p-4 sm:p-6">
                    <CardTitle className="flex items-center gap-2 text-foreground text-lg sm:text-2xl">
                        <ScrollText className="h-5 w-5 sm:h-6 sm:w-6 text-primary"/>
                        System Audit Logs
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm">Comprehensive record of all system activities and security events.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 sm:space-y-6 p-2 sm:p-6">
                    {/* Filters */}
                    <div className="flex flex-col md:flex-row gap-3 sm:gap-4 flex-wrap">
                        <Input 
                            placeholder="Search by User ID or Target ID..." 
                            value={searchQuery} 
                            onChange={(e) => setSearchQuery(e.target.value)} 
                            className="w-full md:flex-1 bg-input text-foreground h-9 sm:h-10 text-sm"
                        />
                         <Select value={selectedAction} onValueChange={(value: AuditAction) => setSelectedAction(value)}>
                            <SelectTrigger className="w-full md:w-[250px] bg-select text-foreground h-9 sm:h-10 text-sm">
                                <SelectValue placeholder="Filter by Action" />
                            </SelectTrigger>
                            <SelectContent className="bg-popover text-popover-foreground max-h-[300px]">
                                <SelectItem value={undefined as any}>All Actions</SelectItem>
                                {Object.values(AuditAction).map(action => (
                                    <SelectItem key={action} value={action}>{action}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button variant="outline" onClick={() => { setSearchQuery(''); setSelectedAction(undefined); }} className="h-9 sm:h-10 text-sm">
                            Reset
                        </Button>
                    </div>

                    <div className="rounded-md border bg-card text-card-foreground shadow-sm overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[150px] text-primary">Timestamp</TableHead>
                                    <TableHead className="w-[200px] text-primary">Action</TableHead>
                                    <TableHead className="w-[150px] text-primary">User</TableHead>
                                    <TableHead className="w-[100px] text-primary">Role</TableHead>
                                    <TableHead className="text-primary">Details</TableHead>
                                    <TableHead className="w-[150px] text-primary">IP Address</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isLoading ? (
                                    Array.from({ length: pageSize }).map((_, i) => (
                                        <TableRow key={i}>
                                            <TableCell colSpan={6}>
                                                <Skeleton className="h-8 w-full" />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : logs.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                                            <p><InfoIcon className="inline-block mr-2 h-5 w-5" /> No logs found.</p>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    logs.map((log) => (
                                        <TableRow key={log.id} className="text-sm">
                                            <TableCell className="font-mono text-xs whitespace-nowrap">
                                                {format(new Date(log.timestamp), 'yyyy-MM-dd HH:mm:ss')}
                                            </TableCell>
                                            <TableCell className="font-semibold text-xs">{log.action}</TableCell>
                                            <TableCell className="font-mono text-xs" title={log.userId || 'system'}>
                                                {log.userId ? `${log.userId.substring(0, 8)}...` : 'system'}
                                            </TableCell>
                                            <TableCell className="capitalize text-xs">{log.userRole ? log.userRole.toLowerCase() : 'unknown'}</TableCell>
                                            <TableCell className="font-mono text-xs max-w-[300px] overflow-hidden text-ellipsis">
                                                {log.targetId && <span className="block text-primary-foreground font-medium mb-1">Target: {log.targetId}</span>}
                                                <span className="text-muted-foreground">{formatDetails(log.details)}</span>
                                            </TableCell>
                                            <TableCell className="font-mono text-xs text-muted-foreground">
                                                {log.ipAddress || 'N/A'}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {/* Pagination */}
                    <div className="flex justify-between items-center pt-4">
                        <p className="text-sm text-muted-foreground">
                            Showing {Math.min(totalLogs, (page - 1) * pageSize + 1)} - {Math.min(totalLogs, page * pageSize)} of {totalLogs} events
                        </p>
                        <div className="flex gap-2">
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setPage(prev => Math.max(1, prev - 1))} 
                                disabled={page === 1}
                            >
                                <ChevronLeft className="h-4 w-4" /> Previous
                            </Button>
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setPage(prev => prev + 1)} 
                                disabled={page >= totalPages}
                            >
                                Next <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default AuditLogsPage;